import {
  Appender,
  AppenderOptions,
  LogEntry,
} from "../../types/LoggerTypes";
import {AppenderType, LogLevel} from "@mosaic/logger-service";

const LevelColors: Record<string, string> = {
  DEBUG: "#3498db", // Blue
  INFO:  "#2ecc71", // Green
  WARN:  "#f1c40f", // Yellow
  ERROR: "#e74c3c", // Red
};

export const STD_PLAIN_APPENDER_TEMPLATE = '[{timestamp}] [{level}] [{component}]: {message}'
export const STD_CONSOLE_APPENDER_NAME = 'STD_CONSOLE_APPENDER_NAME';

export const STD_CONSOLE_APPENDER_DEFAULT_OPTIONS: AppenderOptions = {
  name: STD_CONSOLE_APPENDER_NAME,
  type: AppenderType.CONSOLE,
  template: STD_PLAIN_APPENDER_TEMPLATE
};

/**
 * ConsoleAppender writes formatted log entries to the browser console.
 * It optionally supports styling when the environment console accepts CSS formatting.
 */
export class ConsoleAppender implements Appender {

  private options: AppenderOptions;

  /**
   * Create a ConsoleAppender with provided options merged with defaults.
   */
  constructor(options?: Partial<Omit<AppenderOptions, 'type'>>) {
    this.options = { ...STD_CONSOLE_APPENDER_DEFAULT_OPTIONS, ...(options ?? {}) };
  }

  /**
   * Append an entry to the console. Entries below the configured threshold
   * are discarded (the logger performs the same check, but the appender must
   * honor its own threshold when used directly).
   */
  public append(entry: LogEntry): void {
    if (!this.shouldAppend(entry.level)) return;

    const levelName = LogLevel[entry.level] as unknown as string;
    const color = LevelColors[levelName] || "#ffffff";
    const style = `color: ${color}; font-weight: 500;`;
    const component = entry.component ?? 'global';
    const componentLabel = entry.context?.mfe ?? component;
    const formattedMessage = (this.options.template ?? STD_PLAIN_APPENDER_TEMPLATE)
      .replace('{timestamp}', entry.timestamp)
      .replace('{level}', levelName)
      .replace('{component}', componentLabel)
      .replace('{message}', entry.message)
    // Use browser console styling when available, otherwise fallback to plain log
    try {
       
      console.log(`%c${formattedMessage}`, style, ...(entry.args ?? []));
    } catch (e) {
       
      console.log(formattedMessage, ...(entry.args ?? []));
    }
  }

  private shouldAppend(requestedLevel: LogLevel): boolean {
    const threshold = this.getThreshold();
    if (threshold === LogLevel.OFF) return false;
    return requestedLevel >= threshold;
  }

  /** Return configured threshold for this appender. */
  public getThreshold(): LogLevel {
    return this.options.threshold ?? LogLevel.DEBUG;
  }

  /** Set a new threshold and return the previous one. */
  public setThreshold(newLevel: LogLevel): LogLevel {
    const old = this.options.threshold ?? LogLevel.DEBUG;
    this.options.threshold = newLevel;
    return old;
  }

  /** Return the appender name. */
  public name(): string {
    return this.options.name;
  }

}
